import { useMemo, useCallback, useState } from 'react';
import { useQueries, useQueryClient, UseQueryResult } from 'react-query';
import { SchemaYellowHiveQuote } from 'wg-api-sdk';
import {
  getYellowHiveQuotesJobResult,
  useYellowhiveQuotes,
  getJobResultQKey,
} from '@flows/src/api/yellowhive-flows/getYellowhiveQuotes';
import { JobResult } from '@flows/src/types/Jobs';
import {
  createYellowhiveNavigationMap,
  FlowProductsNavigationMap,
} from './createSidebarNavigationMap';

interface UseYellowhiveQuotesWithPollingParams {
  sessionId?: string;
  enabled?: boolean;
}

/**
 * Hook to get quotes using job polling.
 *
 * Abstracts the async nature of the job polling as a synchronous hook.
 *
 * Job results are stored in the query cache and updated when the job is completed.
 * Recalculating quotes (using different mutation) are also updated here in the query cache.
 *
 *  This could be improved, to investigate
 *  e.g.
 *  - launch new enqueued job when recalculated.
 *  - restructure code so this aggregation of jobs is not necessary. (stop using useQueries and use getYellowHiveQuotesJobResult in child components.)
 *      However in case of yellowhive where we need to wait for all jobs completed, this aggregation is necessary. Or maybe we can improve the aggregation logic.
 *      Checking query cache with `getJobResultQKey` "is anything still running?"
 */
export const useYellowhiveQuotesWithPolling = ({
  sessionId,
  enabled = true,
}: UseYellowhiveQuotesWithPollingParams) => {
  const queryClient = useQueryClient();
  const [navigationMap, setNavigationMap] =
    useState<FlowProductsNavigationMap | null>(null);

  // Initial API call to enqueue quotes
  const {
    data: enqueuedData,
    isLoading: isLoadingEnqueue,
    isError: isErrorEnqueue,
    error: errorEnqueue,
  } = useYellowhiveQuotes({
    sessionId,
    queryOptions: {
      enabled: enabled && !!sessionId,
      onSuccess: (data) => {
        if (data?.quotes) {
          const navMap = createYellowhiveNavigationMap(data.quotes);
          setNavigationMap(navMap);
        }
      },
    },
  });

  const initialEnqueuedQuotes = enqueuedData?.quotes || [];

  const jobQueries = useQueries(
    initialEnqueuedQuotes.map((quote) => {
      const jobId = quote.job.job_id;

      return {
        queryKey: getJobResultQKey(jobId),
        queryFn: () => getYellowHiveQuotesJobResult(jobId),
        enabled: !!jobId,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
        retry: false,
        refetchInterval: (
          data: JobResult<SchemaYellowHiveQuote> | undefined,
        ) => {
          // Stop polling if job is complete
          if (data?.status === 'SUCCEEDED' || data?.status === 'FAILED') {
            return false;
          }
          // Poll every 3 seconds while job is in progress
          return 3000;
        },
      };
    }),
  );

  // Create a map from jobId to query result for efficient lookup
  const quoteJobResultsMap = useMemo(() => {
    const map = new Map<
      string,
      UseQueryResult<JobResult<SchemaYellowHiveQuote>>
    >();
    initialEnqueuedQuotes.forEach((job, index) => {
      const jobId = job.job.job_id;
      if (jobId && jobQueries[index]) {
        map.set(jobId, jobQueries[index]);
      }
    });
    return map;
  }, [initialEnqueuedQuotes, jobQueries]);

  // Check if all jobs are finished
  const areAllJobsFinished = useMemo(() => {
    if (initialEnqueuedQuotes.length === 0) return false;
    return jobQueries.every((query) => {
      const result = query.data;
      return (
        result !== undefined &&
        (result.status === 'SUCCEEDED' || result.status === 'FAILED')
      );
    });
  }, [initialEnqueuedQuotes, jobQueries]);

  // Update the query cache - the map will automatically update since it's derived from jobQueries
  const updateQuote = useCallback(
    (updatedQuote: SchemaYellowHiveQuote) => {
      // When a quote is recalculated, find the corresponding job and update its cache
      const quote = initialEnqueuedQuotes.find(
        (quote) =>
          quote.yellowhive_product_id === updatedQuote.yellowhive_product_id,
      );

      if (quote) {
        const jobId = quote.job.job_id;
        const queryKey = getJobResultQKey(jobId);

        // Update the query cache with the new quote data
        // Only update if oldData exists (which it should since the query is already running)
        const oldData =
          queryClient.getQueryData<JobResult<SchemaYellowHiveQuote>>(queryKey);
        if (oldData) {
          queryClient.setQueryData<JobResult<SchemaYellowHiveQuote>>(queryKey, {
            ...oldData,
            result: updatedQuote,
            status: 'SUCCEEDED' as const,
          });
        }
      }
    },
    [initialEnqueuedQuotes, queryClient],
  );

  return {
    data: {
      quotes: initialEnqueuedQuotes,
      quoteJobResults: quoteJobResultsMap,
      navigationMap,
    },
    isLoading: isLoadingEnqueue || !areAllJobsFinished,
    isError: isErrorEnqueue || jobQueries.some((query) => query.isError),
    error: errorEnqueue || jobQueries.find((query) => query.error)?.error,
    updateQuote,
    areAllJobsFinished,
  };
};
